<template>
  <div class="grid grid-rows-[auto_1fr] h-full bg-base-100 overflow-hidden">
    <ThreadsNav :show-back="true" :show-center="false" />
    <BottomSheetLayout
      :header-height="260"
      :scroll-container-getter="getScrollContainer"
      class="relative w-full overflow-hidden"
      v-if="relationship"
    >
      <template #header>
        <div class="flex flex-col">
          <div class="px-4 mb-4">
            <div>
              <span
                class="text-primary"
                @click="clickMate(relationship.sourceMate)"
              >
                {{ relationship.sourceMate?.name }}
              </span>
              眼中，
              <span
                class="text-primary"
                @click="clickMate(relationship.targetMate)"
              >
                {{ relationship.targetMate?.name }}
              </span>
              是：
            </div>

            <div class="flex font-[Cubic] text-2xl mt-2">
              {{ interactiveStore.getRelationshipTypeName(relationship) }}
            </div>
          </div>

          <!-- Interaction buttons -->
          <div class="p-4">
            <div class="red-dot ml-2 mb-2">
              {{ $t("interactive.actions") }}
            </div>
            <div class="flex flex-wrap gap-2">
              <button class="btn btn-xs btn-outline" @click="sendMail">
                <i class="iconify fluent--mail-16-regular"></i>
                {{ $t("interactive.sendMail") }}
              </button>

              <button
                class="btn btn-xs btn-outline"
                @click="toggleFriend"
                v-if="
                  !relationship.isBlocked &&
                  relationship.type === RelationshipType.Acquaintance
                "
              >
                {{ $t("interactive.inviteFriend") }}
              </button>
              <button class="btn btn-xs btn-outline" @click="inviteTask">
                {{ $t("interactive.inviteTask") }}
              </button>
            </div>
          </div>

          <!-- Interaction settings -->
          <div class="p-4">
            <div class="red-dot ml-2 mb-2">
              {{ $t("interactive.interactionSettings") }}
            </div>
            <div class="flex justify-between items-center">
              <div class="text-sm">
                {{
                  $t("interactive.blockUser", {
                    name: relationship.targetMate?.name,
                  })
                }}
              </div>
              <VanSwitch
                v-model="relationship.isBlocked"
                size="14px"
                @change="handleBlockChange"
              />
            </div>
            <div class="text-xs text-base-content/60 mt-1">
              {{
                $t("interactive.blockDescription", {
                  targetName: relationship.targetMate?.name,
                  sourceName: relationship.sourceMate?.name,
                })
              }}
            </div>
          </div>
        </div>
      </template>
      <template #default="{ isReachMaxHeight }">
        <FilteredInterationEventList
          v-if="
            relationship && relationship.sourceMate && relationship.targetMate
          "
          :source-mate-id="relationship.sourceMate!.id"
          :target-mate-id="relationship.targetMate!.id"
          :enable-pull-load="!isReachMaxHeight"
        />
        <div v-else class="flex-1 flex justify-center items-center">
          <div class="loading loading-spinner loading-md text-primary"></div>
        </div>
      </template>
    </BottomSheetLayout>
    <div v-else class="flex-1 flex justify-center items-center">
      <div class="loading loading-spinner loading-md text-primary"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Switch as VanSwitch,
  showConfirmDialog,
  showLoadingToast,
  showSuccessToast,
  showToast,
} from "vant";
import { useInteractive } from "@/stores/interactive";
import { MateRelationship, Mate } from "@/types/entity";
import { useI18n } from "vue-i18n";
import {
  DispatchType,
  InvitationType,
  RelationshipType,
} from "@/enums/mission";
import { useGlobalModal } from "@/stores/globalModal";
import FilteredInterationEventList from "@/components/interaction-review/FilteredInterationEventList.vue";
import { useRouteQuery } from "@vueuse/router";
import { ApiGetMateRelationshipPage } from "@/apis/interaction";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const interactiveStore = useInteractive();
const modalStore = useGlobalModal();

// Get relationship id from route params
const relationship = ref<MateRelationship | null>(null);
const relationshipId = useRouteQuery<number>("id", 0, {
  transform: (v) => Number(v),
});
const sourceMateId = useRouteQuery<number>("sourceId", 0, {
  transform: (v) => Number(v),
});
const targetMateId = useRouteQuery<number>("targetId", 0, {
  transform: (v) => Number(v),
});

// Fetch relationship when component is mounted
onMounted(async () => {
  let r: MateRelationship | undefined;
  if (relationshipId.value) {
    r = interactiveStore.relationshipList.find(
      (v) => v.id === Number(route.query.id)
    );
  } else if (sourceMateId.value && targetMateId.value) {
    const { page } = await ApiGetMateRelationshipPage({
      pagination: {
        page: 1,
        perPage: 5,
      },
      sort: { field: "dtUpdate", order: "desc" },
      filter: {
        sourceMateId: sourceMateId.value,
        targetMateId: targetMateId.value,
      },
    });
    if (page.data.length > 0) {
      r = page.data[0];
    }
  }
  if (!r) {
    return router.replace({ name: "relationshipList" });
  } else {
    relationship.value = r;
  }
});
// Handle block/unblock change
const handleBlockChange = async (value: boolean) => {
  if (!relationship.value) return;

  try {
    await interactiveStore.updateRelationship(relationship.value.id, {
      isBlocked: value,
    });
    relationship.value.isBlocked = value;

    showSuccessToast(t(`Message.opSuccess`));
  } catch (error) {
    console.error("Failed to update relationship", error);
    showToast({
      message: t("interactive.updateFailed"),
      position: "bottom",
    });
  }
};

// Action functions
const sendMail = () => {
  if (!relationship.value?.sourceMate || !relationship.value?.targetMate)
    return;

  router.push({
    name: "mailEdit",
    query: {
      senderId: relationship.value.sourceMate.id.toString(),
      recipientId: relationship.value.targetMate.id.toString(),
      recipientName: relationship.value.targetMate.name,
    },
  });
};

const clickMate = (mate?: Mate) => {
  if (!mate) return;
  router.push({
    name: "mateHome",
    params: { uuid: mate.uuid },
  });
};

const inviteTask = () => {
  // showToast({
  //   message: t("interactive.featureComingSoon"),
  // });
  router.push({
    name: "missionEdit",
    query: {
      hostUuid: relationship.value?.sourceMate?.uuid,
      inviteeUuid: relationship.value?.targetMate?.uuid,
      type: DispatchType.Mission,
    },
  });
};

const toggleFriend = async () => {
  if (!relationship.value?.sourceMate || !relationship.value?.targetMate)
    return;

  if (relationship.value.type === RelationshipType.Acquaintance) {
    modalStore.openTextInputModal({
      title: "申请添加朋友",
      callback: async (text) => {
        const loading = showLoadingToast({
          message: "申请中...",
          duration: 0,
        });
        try {
          await interactiveStore.createInvitation({
            senderId: relationship.value!.sourceMate!.id,
            recipientId: relationship.value!.targetMate!.id,
            type: InvitationType.Friend,
            message: text,
          });
        } finally {
          loading.close();
        }
      },
    });
  } else if (relationship.value.type === RelationshipType.Friend) {
    // 取消好友
    try {
      await showConfirmDialog({
        message: t("Message.deleteFriendConfirm"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
      await interactiveStore.cancelFriend(relationship.value.id);
      showSuccessToast(t("Message.opSuccess"));
    } catch (error) {
      console.error("Failed to delete relationship", error);
    }
  }
};

const getScrollContainer = () => {
  return document.querySelector(".relationship-event-list") as HTMLElement;
};
</script>
